import React, { useState, useMemo, useEffect } from 'react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { format } from 'date-fns';
import { 
  Star, 
  Users, 
  MapPin, 
  Plus, 
  Vote,
  Calendar,
  Camera,
  Filter,
  Search,
  Heart,
  Lightbulb,
  RotateCcw,
  ArrowUpDown,
  Target,
  Sparkles,
  CheckCircle,
  CalendarDays
} from 'lucide-react';
import AvatarOrInitial from './AvatarOrInitial';
import ActivityRatingSlider from './ActivityRatingSlider';
import { Link } from 'react-router-dom';

export default function ActivityBrowseGrid({ 
  activities, 
  onActivitySelect,
  showFilters = false,
  onToggleFilters,
  onUnarchive,
  showArchived = false,
  onVoteCardClick
}) {
  const { family, currentUser } = useFamilyContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('rating'); // 'rating' or 'newest'
  const [screenSize, setScreenSize] = useState('mobile');

  // Track screen size for responsive voting display
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 768) {
        setScreenSize('mobile');
      } else if (width < 1024) {
        setScreenSize('tablet');
      } else if (width < 1280) {
        setScreenSize('desktop');
      } else {
        setScreenSize('xl');
      }
    };

    handleResize(); // Set initial size
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Find all activities that need voting
  const activitiesNeedingVote = useMemo(() => {
    if (!currentUser || !family) return [];
    
    return activities.filter(activity => {
      // Activity needs voting if:
      // 1. Not scheduled yet
      // 2. Current user hasn't voted
      // 3. Not archived
      return !activity.confirmed_for_calendar && 
             !activity.ratings?.[currentUser.id] &&
             activity.status !== 'archived';
    });
  }, [activities, currentUser, family]);

  // Get the correct number of activities based on screen size
  const getMaxActivities = () => {
    switch (screenSize) {
      case 'mobile': return 1;
      case 'tablet': return 2;
      case 'desktop': return 3;
      case 'xl': return 4;
      default: return 1;
    }
  };

  const displayVoteActivities = activitiesNeedingVote.slice(0, getMaxActivities());

  // Handle vote submission to trigger re-render
  const handleVoteSubmitted = () => {
    // The component will automatically re-render when activities change
    // due to the useMemo dependency on activities
  };

  // Filter and search activities
  const filteredActivities = useMemo(() => {
    let filtered = activities;

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(activity =>
        activity.title.toLowerCase().includes(query) ||
        activity.description?.toLowerCase().includes(query) ||
        activity.category?.toLowerCase().includes(query) ||
        activity.location?.toLowerCase().includes(query)
      );
    }

    // Sort based on selected sort option
    return filtered.sort((a, b) => {
      if (sortBy === 'newest') {
        // Sort by creation date only (newest first)
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      } else {
        // Sort by family rating, then by creation date (default)
        const aRatings = Object.values(a.ratings || {});
        const bRatings = Object.values(b.ratings || {});
        
        const aAvg = aRatings.length > 0 ? aRatings.reduce((sum, r) => sum + r, 0) / aRatings.length : 0;
        const bAvg = bRatings.length > 0 ? bRatings.reduce((sum, r) => sum + r, 0) / bRatings.length : 0;
        
        if (aAvg !== bAvg) return bAvg - aAvg; // Higher rating first
        
        return new Date(b.created_at || 0) - new Date(a.created_at || 0); // Newer first for ties
      }
    });
  }, [activities, searchQuery, sortBy]);

  // Get family average rating for an activity
  const getFamilyRating = (activity) => {
    const ratings = Object.values(activity.ratings || {});
    if (ratings.length === 0) return null;
    
    const average = ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length;
    return {
      average: average.toFixed(1),
      count: ratings.length,
      totalFamily: family ? (family.parents?.length || 0) + (family.kids?.length || 0) : 1
    };
  };

  // Get activity category emoji
  const getCategoryEmoji = (category) => {
    const emojiMap = {
      food: '🍕',
      entertainment: '🎬',
      outdoor: '🌳',
      sports: '⚽',
      cultural: '🎨',
      educational: '📚',
      social: '👥',
      creative: '🎨',
      adventure: '🏔️',
      relaxation: '🧘',
      shopping: '🛍️',
      other: '🎯'
    };
    return emojiMap[category] || '🎯';
  };

  // Format category display name (e.g., "dining-out" -> "Dining Out")
  const formatCategoryDisplay = (category) => {
    if (!category) return 'Other';
    
    // Handle special cases
    const specialCases = {
      'dining-out': 'Dining Out',
      'casual-bites': 'Casual Bites',
      'food': 'Food & Dining',
      'sports': 'Sports & Fitness',
      'cultural': 'Arts & Culture'
    };
    
    if (specialCases[category.toLowerCase()]) {
      return specialCases[category.toLowerCase()];
    }
    
    // General formatting: replace hyphens/underscores with spaces and capitalize
    return category
      .replace(/[-_]/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Format activity dates
  const formatActivityDate = (activity) => {
    if (activity.completed_date) {
      return { type: 'completed', text: `Done ${format(new Date(activity.completed_date), 'MMM d')}` };
    }
    if (activity.start_date) {
      const startDate = new Date(activity.start_date);
      if (activity.end_date && activity.end_date !== activity.start_date) {
        const endDate = new Date(activity.end_date);
        return { 
          type: 'range', 
          text: `${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d')}` 
        };
      }
      return { type: 'single', text: format(startDate, 'MMM d, yyyy') };
    }
    if (activity.date) {
      return { type: 'single', text: format(new Date(activity.date), 'MMM d, yyyy') };
    }
    return null;
  };

  // Handle activity actions
  const handleCardClick = (activity, event) => {
    const clickedElement = event.currentTarget;
    onActivitySelect?.(activity, clickedElement);
  };

  // Handle vote card click to show details
  const handleVoteCardClick = (activity, event) => {
    const clickedElement = event.currentTarget;
    onVoteCardClick?.(activity, clickedElement);
  };

  if (filteredActivities.length === 0) {
    return (
      <div className="browse-section">
        {/* Search Bar */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <input
            type="text"
            placeholder="Search activities..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-gold-500"
          />
          {onToggleFilters && (
            <button
              onClick={onToggleFilters}
              className={`absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg transition-colors ${
                showFilters ? 'bg-gold-500 text-white' : 'text-stone-400 hover:text-stone-600'
              }`}
            >
              <Filter className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Empty State */}
        <div className="empty-state">
          <Lightbulb className="empty-state-icon" />
          <div className="empty-state-title">
            {searchQuery ? 'No activities found' : 'No activities yet'}
          </div>
          <div className="empty-state-description">
            {searchQuery 
              ? `No activities match "${searchQuery}". Try a different search term.`
              : 'Start by adding some things to do for your family to vote on!'
            }
          </div>
          <div className="empty-state-actions">
            {searchQuery ? (
              <button 
                onClick={() => setSearchQuery('')}
                className="empty-state-btn secondary"
              >
                Clear Search
              </button>
            ) : (
              <button className="empty-state-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Activity Idea
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="browse-section">
      {/* Family Activity Vote Section - Responsive Grid */}
      {activitiesNeedingVote.length > 0 && !showArchived && (
        <div className="bg-gradient-to-br from-gold-400 to-gold-600 text-slate-900 rounded-2xl p-4 mb-6 relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute w-32 h-32 bg-white bg-opacity-10 rounded-full -top-16 -right-16"></div>
          
          <div className="relative z-10">
            {/* Vote Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Vote className="w-5 h-5" />
                <span className="font-bold text-lg">Family Activity Vote</span>
              </div>
              <span className="px-3 py-1 bg-white/20 rounded-full text-sm font-semibold">
                {activitiesNeedingVote.length} total to vote
              </span>
            </div>

            {/* Single Row Grid of Voting Widgets */}
            <div className={`grid gap-4 ${
              screenSize === 'mobile' ? 'grid-cols-1' :
              screenSize === 'tablet' ? 'grid-cols-2' :
              screenSize === 'desktop' ? 'grid-cols-3' :
              'grid-cols-4'
            }`}>
              {displayVoteActivities.map((activity) => {
                const familyRatings = Object.values(activity.ratings || {});
                const averageRating = familyRatings.length > 0 
                  ? (familyRatings.reduce((sum, rating) => sum + rating, 0) / familyRatings.length).toFixed(1)
                  : 0;
                const isNewIdea = familyRatings.length === 0;

                return (
                  <div
                    key={activity.id}
                    className={`rounded-2xl p-4 border shadow-sm border-l-4 cursor-pointer hover:shadow-md transition-shadow ${
                      isNewIdea 
                        ? 'bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200 border-l-emerald-500' 
                        : 'bg-white border-stone-200 border-l-amber-500'
                    }`}
                    onClick={(e) => handleVoteCardClick(activity, e)}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-bold text-slate-900">
                          {isNewIdea ? 'New!' : 'Vote'}
                        </h3>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        isNewIdea 
                          ? 'bg-emerald-500 text-white animate-pulse' 
                          : 'bg-amber-500 text-white'
                      }`}>
                        {isNewIdea ? 'NEW!' : 'Vote'}
                      </span>
                    </div>
                    
                    {activity.image ? (
                      <img 
                        src={activity.image} 
                        alt={activity.title}
                        className="w-full h-24 object-cover rounded-xl mb-3 border border-stone-200"
                      />
                    ) : (
                      <div className="w-full h-24 bg-stone-100 rounded-xl mb-3 border border-stone-200 flex items-center justify-center">
                        <Target className="w-6 h-6 text-stone-400" />
                      </div>
                    )}
                    
                    <h4 className="text-sm font-bold text-slate-900 mb-1 line-clamp-1">{activity.title}</h4>
                    <p className="text-stone-500 mb-2 text-xs line-clamp-2">{activity.description || 'Vote on this family activity'}</p>
                    
                    {/* Show date if available */}
                    {formatActivityDate(activity) && (
                      <div className="flex items-center gap-1 text-xs text-stone-600 mb-2">
                        <CalendarDays className="w-3 h-3" />
                        <span>{formatActivityDate(activity).text}</span>
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-center">
                        <div className="text-xs text-stone-500 font-semibold mb-1">
                          Family: <strong className="text-slate-900">{averageRating}</strong>
                        </div>
                        <div className="w-16 h-1.5 bg-stone-200 rounded-full mx-auto">
                          <div 
                            className="h-1.5 bg-amber-500 rounded-full transition-all"
                            style={{ width: `${(averageRating / 10) * 100}%` }}
                          />
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-extrabold text-slate-900">
                          {activity.ratings?.[currentUser?.id] || '?'}
                        </div>
                        <div className="text-xs text-stone-500 font-semibold">Your Rating</div>
                      </div>
                    </div>
                    
                                         <div className="mb-3" onClick={(e) => e.stopPropagation()}>
                       <ActivityRatingSlider
                         activity={activity}
                         currentUser={currentUser}
                         family={family}
                         compact={true}
                         onVoteSubmit={handleVoteSubmitted}
                       />
                     </div>
                    
                    {/* Enhanced call-to-action for new activities */}
                    {isNewIdea && (
                      <div className="p-2 bg-emerald-100 border border-emerald-200 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1">
                            <Sparkles className="w-3 h-3 text-emerald-600" />
                            <span className="text-xs font-medium text-emerald-800">
                              Be first to vote!
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                                 );
               })}
             </div>
             
             {/* Show indicator if there are more activities waiting */}
             {activitiesNeedingVote.length > getMaxActivities() && (
               <div className="mt-4 text-center">
                 <div className="inline-flex items-center gap-2 px-3 py-2 bg-white/20 rounded-full text-sm font-medium">
                   <span>+{activitiesNeedingVote.length - getMaxActivities()} more activities waiting</span>
                 </div>
               </div>
             )}
           </div>
         </div>
       )}

      {/* Search and Controls - Responsive */}
      <div className="section-header">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div className="flex-1 min-w-0">
            <div className="section-title">Browse Things to Do</div>
            <div className="section-subtitle text-xs sm:text-sm">or add your own with quick add!</div>
          </div>
          
          <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-3">
            <span className="text-xs sm:text-sm text-stone-600 whitespace-nowrap">
              {filteredActivities.length} activities
            </span>
            
            {/* Sort Dropdown */}
            <div className="relative">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="appearance-none bg-white border border-stone-200 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 pr-6 sm:pr-8 text-xs sm:text-sm text-stone-700 hover:border-stone-300 focus:outline-none focus:ring-2 focus:ring-gold-500 focus:border-gold-500 min-w-0"
              >
                <option value="rating">Best Rated</option>
                <option value="newest">Newest</option>
              </select>
              <ArrowUpDown className="absolute right-1.5 sm:right-2 top-1/2 -translate-y-1/2 w-3 sm:w-4 h-3 sm:h-4 text-stone-400 pointer-events-none" />
            </div>
            
            {onToggleFilters && (
              <button
                onClick={onToggleFilters}
                className={`p-1.5 sm:p-2 rounded-lg transition-colors flex-shrink-0 ${
                  showFilters ? 'bg-gold-500 text-white' : 'text-stone-400 hover:text-stone-600'
                }`}
              >
                <Filter className="w-3 sm:w-4 h-3 sm:h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
        <input
          type="text"
          placeholder="Search activities..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-gold-500"
        />
      </div>

      {/* Activity Grid - Horizontal cards in responsive grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {filteredActivities.map((activity) => {
          const familyRating = getFamilyRating(activity);
          const categoryEmoji = getCategoryEmoji(activity.category);
          
          return (
            <div
              key={activity.id}
              onClick={(e) => handleCardClick(activity, e)}
              className="bg-white rounded-xl border border-stone-200 hover:border-stone-300 hover:shadow-md transition-all duration-200 cursor-pointer overflow-hidden flex flex-col h-full"
            >
              {/* Activity Image - Top */}
              <div className="w-full h-32 flex-shrink-0 relative">
                {activity.image ? (
                  <img 
                    src={activity.image} 
                    alt={activity.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-stone-100 to-stone-200 flex items-center justify-center">
                    <span className="text-3xl">{categoryEmoji}</span>
                  </div>
                )}
                
                {/* Completed Badge */}
                {activity.completed_date && (
                  <div className="absolute top-2 left-2 bg-emerald-500/90 backdrop-blur-sm text-white rounded-full px-2 py-1 flex items-center gap-1 text-xs font-medium">
                    <CheckCircle className="w-3 h-3" />
                    <span>Done</span>
                  </div>
                )}
                
                {/* Favorite Badge */}
                {activity.is_favorite && (
                  <div className="absolute top-2 left-14 bg-yellow-500/90 backdrop-blur-sm text-white rounded-full px-2 py-1 flex items-center gap-1 text-xs font-medium">
                    <Heart className="w-3 h-3 fill-current" />
                    <span>Favorite</span>
                  </div>
                )}
                
                {/* Rating Badge */}
                {familyRating && !activity.completed_date && (
                  <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm rounded-full px-2 py-1 flex items-center gap-1 text-xs font-medium">
                    <Star className="w-3 h-3 text-yellow-400 fill-current" />
                    <span>{familyRating.average}</span>
                    <span className="text-stone-500">({familyRating.count})</span>
                  </div>
                )}
                
                {/* Vote Needed Indicator */}
                {!familyRating && (
                  <div className="absolute top-2 right-2 bg-azure-500/90 backdrop-blur-sm text-white rounded-full px-2 py-1 flex items-center gap-1 text-xs">
                    <Vote className="w-3 h-3" />
                    <span>Vote</span>
                  </div>
                )}
              </div>

              {/* Activity Info - Bottom */}
              <div className="p-3 flex flex-col justify-between flex-1 min-h-0">
                <div className="flex-1">
                  <h3 className="font-semibold text-stone-900 text-sm leading-tight mb-1 overflow-hidden" style={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical'
                  }}>
                    {activity.title}
                  </h3>
                  
                  {activity.description && (
                    <p className="text-stone-600 text-xs leading-relaxed mb-2 overflow-hidden" style={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical'
                    }}>
                      {activity.description}
                    </p>
                  )}
                  
                  {activity.location && (
                    <div className="flex items-center gap-1 text-stone-600 text-xs mb-1">
                      <MapPin className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">{activity.location}</span>
                    </div>
                  )}
                  
                  {/* Show date on browse cards */}
                  {formatActivityDate(activity) && (
                    <div className="flex items-center gap-1 text-stone-600 text-xs">
                      {activity.completed_date ? (
                        <CheckCircle className="w-3 h-3 text-emerald-600 flex-shrink-0" />
                      ) : (
                        <Calendar className="w-3 h-3 flex-shrink-0" />
                      )}
                      <span className="truncate">{formatActivityDate(activity).text}</span>
                    </div>
                  )}
                </div>
                
                {/* Bottom info */}
                <div className="flex items-center justify-between mt-2 pt-2 border-t border-stone-100 flex-shrink-0">
                  <div className="flex items-center gap-1">
                    <div className="bg-stone-100 text-stone-600 px-2 py-1 rounded-full text-xs font-medium truncate">
                      {formatCategoryDisplay(activity.category)}
                    </div>
                    {activity.is_favorite && (
                      <Heart className="w-3 h-3 text-yellow-500 fill-current" />
                    )}
                  </div>
                  
                  {/* Action indicator */}
                  {showArchived ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent card click
                        onUnarchive?.(activity);
                      }}
                      className="text-amber-600 hover:text-amber-800 hover:bg-amber-50 p-1 rounded transition-colors flex-shrink-0"
                      title="Restore activity"
                    >
                      <RotateCcw className="w-4 h-4" />
                    </button>
                  ) : (
                    <div className="text-stone-400 group-hover:text-stone-600 transition-colors flex-shrink-0">
                      <Plus className="w-4 h-4" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Load More / Pagination could go here */}
      {filteredActivities.length > 0 && (
        <div className="text-center mt-6">
          <p className="text-sm text-stone-600">
            Showing {filteredActivities.length} activities
          </p>
        </div>
      )}
    </div>
  );
}